import React, { useEffect, useRef } from 'react';

import styled from '@emotion/styled';
import { AnimationItem } from 'lottie-web';

interface LottieAnimationProps {
  animationData: any;
  loop?: boolean;
  autoplay?: boolean;
  renderer?: 'svg' | 'canvas' | 'html';
  className?: string;
}

const Container = styled.div`
  width: 100%;
  height: 100%;
`;

export const LottieAnimation: React.FC<LottieAnimationProps> = ({
  animationData,
  loop = true,
  autoplay = true,
  renderer = 'svg',
  className,
}) => {
  const animationContainer = useRef<HTMLDivElement>(null);
  const lottieInstance = useRef<AnimationItem | null>(null);

  useEffect(() => {
    const container = animationContainer.current;
    if (!container || !animationData) {
      return;
    }

    let destroyed = false;

    // Dynamic import to avoid SSR issues - same pattern as Lottie.tsx
    import('lottie-web')
      .then((lottieWeb) => {
        if (destroyed || !container) {
          return;
        }

        lottieInstance.current = lottieWeb.default.loadAnimation({
          container: container,
          renderer: renderer,
          loop: loop,
          autoplay: autoplay,
          animationData: animationData,
        });
      })
      .catch((e) => {
        if (e.name !== 'ChunkLoadError') {
          console.error('Failed to load lottie-web:', e);
        }
      });

    return () => {
      destroyed = true;
      lottieInstance.current?.destroy();
    };
  }, [animationData, loop, autoplay, renderer]);

  return <Container ref={animationContainer} className={className} />;
};

export default LottieAnimation;
